<template>
  <v-container fluid>
    <!-- Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <h2 class="text-h5 font-weight-medium">
          Templates d'Interventions ({{ totalTemplates }})
        </h2>
      </v-col>

      <v-col cols="auto">
        <v-btn color="primary" prepend-icon="mdi-plus" @click="createTemplate">
          Nouveau Template
        </v-btn>
      </v-col>
    </v-row>

    <!-- List -->
    <v-card rounded="xl" elevation="2">
      <v-data-table :items="templates" :loading="loading" item-key="id" hover class="intervention-table">
        <!-- Product Type -->
        <template #item.productType="{ item }">
          <div class="clickable-row" @click="goToEditor(item)">
            <div class="font-weight-medium">
              {{ item.productType }}
            </div>
            <div class="text-caption text-medium-emphasis">
              {{ item.items.length }} items
            </div>
          </div>
        </template>

        <!-- Mandatory count -->
        <template #item.items="{ item }">
          <v-chip size="small" color="primary" variant="tonal">
            {{
              item.items.filter(i => i.required).length
            }}
            mandatory
          </v-chip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <v-btn icon="mdi-delete" variant="text" color="error" @click.stop="askDelete(item)" />
        </template>

        <template #headers>
          <tr>
            <th>Type de Produit</th>
            <th>Etapes Obligatoires</th>
            <th class="text-right">Actions</th>
          </tr>
        </template>

        <template #item="{ item }">
          <tr class="table-row" @click="goToEditor(item)">
            <td>
              <div class="font-weight-medium">
                {{ item.productType }}
              </div>
              <div class="text-caption text-medium-emphasis">
                {{ item.items.length }} étapes
              </div>
            </td>

            <td>
              <v-chip size="small" color="primary" variant="tonal">
                {{
                  item.items.filter(i => i.required).length
                }}
                requis
              </v-chip>
            </td>

            <td class="text-right">
              <v-btn icon="mdi-delete" variant="text" color="error" @click.stop="askDelete(item)" />
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Delete confirmation -->
    <v-dialog :scrim="true" v-model="deleteDialog" max-width="420">
      <v-card rounded="xl">
        <v-card-title class="text-h6">
          Suppression template?
        </v-card-title>

        <v-card-text>
          Cette action est irréversible.
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteDialog = false">
            Annuler
          </v-btn>
          <v-btn color="error" @click="confirmDelete">
            Supprimer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>


<script setup lang="ts">
import { useAppStore } from '@/store'
import { InterventionTemplate } from '@/types'
import { deleteInterventionTemplate, fetchInterventionTemplates } from '@/utils/intervention-template-api'
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'


const router = useRouter();
const appStore = useAppStore();

const templates = ref<InterventionTemplate[]>([]);
const loading = ref(false);
const deleteDialog = ref(false);
const templateToDelete = ref<InterventionTemplate | null>(null);

  const totalTemplates = computed(() => templates.value.length);

async function fetchTemplates() {
  loading.value = true;
  try {
    templates.value = await fetchInterventionTemplates();
  } finally {
    loading.value = false;
  }
}

function goToEditor(template: InterventionTemplate) {
  router.push({
    name: 'InterventionTemplateEditor',
    params: { id: template.id }
  })
}

function createTemplate() {
  router.push({
    name: 'InterventionTemplateEditor',
    params: { id: 'new' }
  })
}

function askDelete(template: InterventionTemplate) {
  templateToDelete.value = template;
  deleteDialog.value = true;
}

async function confirmDelete() {
  deleteDialog.value = false
  if (!templateToDelete.value) {
    return;
  }
  try {
    loading.value = true
    await deleteInterventionTemplate(templateToDelete.value.id!);
    appStore.sendSuccessNotice('Template has been deleted.');
    templateToDelete.value = null;
    fetchTemplates();
  } catch (e) {
    // Ignore because it's handled in axios service
  }
  finally {
    loading.value = false
  }
}


onMounted(fetchTemplates)
</script>

<style scoped>
.table-row {
  cursor: pointer;
  transition: background 0.15s ease;
}

.table-row:hover {
  background: rgba(var(--v-theme-primary), 0.05);
}
</style>
